(() => {
  if (window.__fashiontigerGlobalProductCards) return;
  window.__fashiontigerGlobalProductCards = true;

  const getCards = () => Array.from(document.querySelectorAll('[data-ft-product-card].ft-product-card--conversion, [data-ft-product-card].ft-product-card--metro'));

  const normalizeWishlistButtons = () => {
    const selector = [
      'wishlist-button-collection',
      'xb-wishlist-button-collection',
      '[data-wishlist-button]',
      '.wk-button',
      '.wk-button-collection',
      '.swym-button',
      '[class*="wishlist-button-collection"]',
      '[class*="swish-wishlist"]'
    ].join(',');

    getCards().forEach((card) => {
      const slot = card.querySelector('[data-ft-wishlist-slot]');
      if (!slot) return;
      const rawCandidates = Array.from(card.querySelectorAll(selector)).filter((node) => node !== slot);
      const roots = rawCandidates.filter((node) => !rawCandidates.some((other) => other !== node && other.contains(node)));
      if (!roots.length) return;
      const chosen = roots.find((node) => {
        const rect = node.getBoundingClientRect();
        return rect.width > 0 || rect.height > 0;
      }) || roots[0];

      roots.forEach((node) => {
        if (node === chosen) {
          node.classList.remove('ft-wishlist-duplicate');
          return;
        }
        node.classList.add('ft-wishlist-duplicate');
        node.setAttribute('aria-hidden', 'true');
      });

      if (chosen.parentElement !== slot) slot.appendChild(chosen);
      chosen.classList.add('ft-wishlist-fixed');
      chosen.removeAttribute('aria-hidden');

      const stateNodes = [chosen, ...chosen.querySelectorAll('button, a, [aria-pressed], [aria-selected], [data-state], [data-active], [class]')];
      const isWishlisted = stateNodes.some((node) => {
        const className = String(node.className || '').toLowerCase();
        const ariaPressed = node.getAttribute?.('aria-pressed');
        const ariaSelected = node.getAttribute?.('aria-selected');
        const dataState = String(node.getAttribute?.('data-state') || '').toLowerCase();
        const dataActive = String(node.getAttribute?.('data-active') || '').toLowerCase();
        const label = `${node.getAttribute?.('aria-label') || ''} ${node.getAttribute?.('title') || ''}`.toLowerCase();
        return ariaPressed === 'true'
          || ariaSelected === 'true'
          || dataActive === 'true'
          || ['active', 'selected', 'added', 'wishlisted', 'in-wishlist'].includes(dataState)
          || /(^|[\s_-])(is-)?(wishlisted|selected|added|active|filled)([\s_-]|$)/.test(className)
          || label.includes('remove from wishlist')
          || label.includes('remove from favorites')
          || label.includes('remove from favourites')
          || label.includes('verwijder uit wishlist')
          || label.includes('verwijder uit favorieten');
      });
      slot.classList.toggle('is-wishlisted', isWishlisted);
    });
  };

  const normalizeFamilyKey = (value = '') => value.trim().toLowerCase().replace(/\s+/g, ' ');
  const getFamilyContainers = () => Array.from(document.querySelectorAll('[data-ft-family-swatches]'));
  const familyCache = new Map();
  const familyRemoteStarted = new Set();
  const renderedFamilyEntries = new WeakMap();
  const observedCardSwatches = new WeakSet();
  const cardSwatchWidths = new WeakMap();
  const cardSwatchResizeObserver = 'ResizeObserver' in window
    ? new ResizeObserver((entries) => {
        entries.forEach(({ target }) => {
          const container = target.querySelector('.ft-family-swatches--card[data-ft-family-swatches]');
          if (!container) return;
          const width = Math.round(target.getBoundingClientRect().width);
          if (cardSwatchWidths.get(container) === width) return;
          cardSwatchWidths.set(container, width);
          const currentEntries = renderedFamilyEntries.get(container);
          if (currentEntries) renderFamilySwatches(container, currentEntries);
        });
      })
    : null;

  const productPath = (url) => {
    try { return new URL(url, window.location.origin).pathname.replace(/\/$/, ''); }
    catch (error) { return String(url || '').split('?')[0].replace(/\/$/, ''); }
  };

  const entryFromNode = (node) => ({
    node,
    baseTitle: node.dataset.ftBaseTitle || '',
    colorLabel: node.dataset.ftColorLabel || 'Kleur',
    url: node.dataset.ftProductUrl || node.getAttribute('href') || '',
    previewImage: node.dataset.ftPreviewImage || ''
  });

  const dedupeFamilyEntries = (entries) => {
    const seen = new Set();
    return entries.filter((entry) => {
      const key = productPath(entry.url);
      if (!key || seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  };

  const collectLocalFamilyEntries = (baseKey) => {
    const nodes = Array.from(document.querySelectorAll('[data-ft-family-swatch-entry]'));
    const discovered = nodes
      .map(entryFromNode)
      .filter((entry) => normalizeFamilyKey(entry.baseTitle) === baseKey);
    const remembered = getFamilyContainers()
      .filter((container) => normalizeFamilyKey(container.dataset.ftBaseTitle || '') === baseKey)
      .flatMap((container) => renderedFamilyEntries.get(container) || []);
    return dedupeFamilyEntries([...discovered, ...remembered]);
  };

  const fetchFamilyEntries = (baseTitle) => {
    const baseKey = normalizeFamilyKey(baseTitle);
    if (!baseKey) return Promise.resolve([]);
    if (familyCache.has(baseKey)) return familyCache.get(baseKey);

    const root = window.Shopify?.routes?.root || '/';
    const searchUrl = `${root}search?q=${encodeURIComponent(baseTitle)}&type=product&view=ft-swatches`;
    const controller = 'AbortController' in window ? new AbortController() : null;
    const timeoutId = window.setTimeout(() => controller?.abort(), 4500);
    const promise = fetch(searchUrl, {
      credentials: 'same-origin',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
      signal: controller?.signal
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Fashiontiger swatches: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return dedupeFamilyEntries(Array.from(doc.querySelectorAll('[data-ft-swatch-result]'))
          .map(entryFromNode)
          .filter((entry) => normalizeFamilyKey(entry.baseTitle) === baseKey));
      })
      .catch(() => [])
      .finally(() => window.clearTimeout(timeoutId));

    familyCache.set(baseKey, promise);
    return promise;
  };

  const renderFamilySwatches = (container, entries) => {
    const row = container.querySelector('[data-ft-swatch-row]');
    if (!row) return;

    const currentPath = productPath(container.dataset.ftCurrentUrl || window.location.pathname);
    const limit = Math.max(0, parseInt(container.dataset.ftLimit || '0', 10) || 0);
    const allEntries = dedupeFamilyEntries(entries);
    const isCard = container.classList.contains('ft-family-swatches--card');
    const currentEntry = allEntries.find((entry) => productPath(entry.url) === currentPath);
    renderedFamilyEntries.set(container, allEntries);

    let effectiveLimit = limit > 0 ? limit : allEntries.length;
    if (isCard) {
      const info = container.closest('.product-card__info');
      const rowWrap = container.closest('.ft-grid-card-colors-row') || container.closest('.ft-grid-commerce-row');
      if (info && cardSwatchResizeObserver && !observedCardSwatches.has(info)) {
        cardSwatchResizeObserver.observe(info);
        observedCardSwatches.add(info);
      }
      const totalWidth = rowWrap?.getBoundingClientRect().width || 0;
      if (totalWidth > 0) {
        const quickShop = rowWrap?.querySelector('.ft-grid-quickshop-wrap');
        const quickWidth = quickShop?.getBoundingClientRect().width || 0;
        const colorLabel = rowWrap?.querySelector('.ft-grid-card-color');
        const labelWidth = colorLabel?.getBoundingClientRect().width || 0;
        const gridGap = (quickShop || colorLabel) ? (parseFloat(getComputedStyle(rowWrap).columnGap) || 6) : 0;
        const available = Math.max(0, totalWidth - quickWidth - labelWidth - gridGap - 8);
        const rowStyle = getComputedStyle(row);
        const sample = row.querySelector('.ft-family-swatch__link');
        const size = sample ? (parseFloat(getComputedStyle(sample).width) || 11) : 11;
        const gap = parseFloat(rowStyle.gap) || 2;
        const cell = size + gap;
        const allFit = Math.max(1, Math.floor((available + gap) / cell));
        const withMore = Math.max(1, Math.floor((available - 24 + gap) / cell));
        const fit = allEntries.length > allFit ? withMore : allFit;
        effectiveLimit = Math.max(1, Math.min(effectiveLimit, fit));
      }
    }

    let visible = allEntries.slice(0, effectiveLimit);
    if (currentEntry && !visible.some((entry) => productPath(entry.url) === currentPath)) {
      if (visible.length >= effectiveLimit) visible[visible.length - 1] = currentEntry;
      else visible.push(currentEntry);
      visible = dedupeFamilyEntries(visible);
    }

    const signature = `${allEntries.map((entry) => productPath(entry.url)).join('|')}::${currentPath}::${effectiveLimit}`;
    if (row.dataset.ftSignature === signature) {
      container.classList.add('is-ready');
      return;
    }

    const fragment = document.createDocumentFragment();
    visible.forEach((entry) => {
      const link = entry.node.cloneNode(true);
      link.classList.remove('is-current');
      link.removeAttribute('aria-current');
      link.dataset.ftFamilySwatchLink = '';
      link.dataset.ftFamilySwatchEntry = '';
      const isCurrent = productPath(entry.url) === currentPath;
      if (isCurrent) {
        link.classList.add('is-current');
        link.setAttribute('aria-current', 'page');
      }
      fragment.appendChild(link);
    });

    const hiddenCount = Math.max(0, allEntries.length - visible.length);
    if (hiddenCount > 0) {
      const more = document.createElement('span');
      more.className = 'ft-family-swatch__more';
      more.textContent = `+${hiddenCount}`;
      more.setAttribute('aria-label', `${hiddenCount} meer kleuren`);
      more.title = `${hiddenCount} meer kleuren`;
      fragment.appendChild(more);
    }

    row.replaceChildren(fragment);
    row.dataset.ftSignature = signature;
    container.classList.add('is-ready');
  };

  const fetchAndRenderFamily = (baseTitle, baseKey, groupContainers, localEntries) => {
    if (!baseKey || familyRemoteStarted.has(baseKey)) return;
    familyRemoteStarted.add(baseKey);
    fetchFamilyEntries(baseTitle).then((remoteEntries) => {
      const freshLocalEntries = collectLocalFamilyEntries(baseKey);
      const merged = dedupeFamilyEntries([...freshLocalEntries, ...localEntries, ...remoteEntries]);
      groupContainers.forEach((container) => {
        if (document.documentElement.contains(container)) renderFamilySwatches(container, merged);
      });
    });
  };

  const hydrateFamilySwatches = () => {
    const containers = getFamilyContainers();
    if (!containers.length) return;

    const groups = new Map();
    containers.forEach((container) => {
      const baseTitle = container.dataset.ftBaseTitle || '';
      const baseKey = normalizeFamilyKey(baseTitle);
      if (!baseKey) return;
      if (!groups.has(baseKey)) groups.set(baseKey, { baseTitle, containers: [] });
      groups.get(baseKey).containers.push(container);
    });

    groups.forEach(({ baseTitle, containers: groupContainers }, baseKey) => {
      const localEntries = collectLocalFamilyEntries(baseKey);
      groupContainers.forEach((container) => renderFamilySwatches(container, localEntries));

      // PDP: one targeted request so all sibling colours are immediately available.
      const pdpContainer = groupContainers.find((container) => container.classList.contains('ft-family-swatches--pdp'));
      if (pdpContainer) fetchAndRenderFamily(baseTitle, baseKey, groupContainers, localEntries);
    });
  };

  const hydrateRemoteFamilyForContainer = (container) => {
    if (!container || container.classList.contains('ft-family-swatches--pdp')) return;
    const baseTitle = container.dataset.ftBaseTitle || '';
    const baseKey = normalizeFamilyKey(baseTitle);
    if (!baseKey || familyRemoteStarted.has(baseKey)) return;
    const groupContainers = getFamilyContainers().filter((item) => normalizeFamilyKey(item.dataset.ftBaseTitle || '') === baseKey);
    const localEntries = collectLocalFamilyEntries(baseKey);
    fetchAndRenderFamily(baseTitle, baseKey, groupContainers, localEntries);
  };

  const getCardPreviewImage = (card) => card?.querySelector('.product-card__image--main img:not(.ft-swatch-preview-image)') || null;
  const getCollectionPreviewLayer = (card) => {
    if (!card?.closest('[data-ft-collection-v2]')) return null;
    const media = card.querySelector('.product-card__image--main');
    const original = getCardPreviewImage(card);
    if (!media || !original) return null;
    let layer = media.querySelector('.ft-swatch-preview-image');
    if (!layer) {
      layer = document.createElement('img');
      layer.className = 'ft-swatch-preview-image';
      layer.alt = '';
      layer.setAttribute('aria-hidden', 'true');
      layer.decoding = 'async';
      media.appendChild(layer);
    }
    return layer;
  };
  const previewSwatchProductImage = (link) => {
    if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;
    const previewUrl = link?.dataset?.ftPreviewImage;
    const card = link?.closest('[data-ft-product-card]');
    const image = getCardPreviewImage(card);
    if (!previewUrl || !card || !image) return;

    const collectionLayer = getCollectionPreviewLayer(card);
    if (collectionLayer) {
      const token = String((Number(card.dataset.ftPreviewToken || 0) + 1));
      card.dataset.ftPreviewToken = token;
      card.classList.add('is-previewing-color');

      const showNewPreview = () => {
        const loader = new Image();
        loader.onload = () => {
          if (card.dataset.ftPreviewToken !== token) return;
          collectionLayer.src = previewUrl;
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (card.dataset.ftPreviewToken === token) collectionLayer.classList.add('is-visible');
            });
          });
        };
        loader.src = previewUrl;
      };

      if (collectionLayer.classList.contains('is-visible') && collectionLayer.src && !collectionLayer.src.endsWith(previewUrl)) {
        collectionLayer.classList.remove('is-visible');
        window.setTimeout(() => {
          if (card.dataset.ftPreviewToken === token) showNewPreview();
        }, 180);
      } else if (!collectionLayer.src || !collectionLayer.src.endsWith(previewUrl)) {
        showNewPreview();
      } else {
        collectionLayer.classList.add('is-visible');
      }
      return;
    }

    if (!image.dataset.ftOriginalSrc) {
      image.dataset.ftOriginalSrc = image.getAttribute('src') || '';
      image.dataset.ftOriginalSrcset = image.getAttribute('srcset') || '';
      image.dataset.ftOriginalSizes = image.getAttribute('sizes') || '';
    }
    image.removeAttribute('srcset');
    image.removeAttribute('sizes');
    image.src = previewUrl;
    card.classList.add('is-previewing-color');
  };
  const restoreSwatchProductImage = (card) => {
    if (!card) return;
    const collectionLayer = card.querySelector('.ft-swatch-preview-image');
    if (collectionLayer) {
      card.dataset.ftPreviewToken = String((Number(card.dataset.ftPreviewToken || 0) + 1));
      collectionLayer.classList.remove('is-visible');
      window.setTimeout(() => {
        if (!collectionLayer.classList.contains('is-visible')) collectionLayer.removeAttribute('src');
      }, 560);
      card.classList.remove('is-previewing-color');
      return;
    }

    const image = getCardPreviewImage(card);
    if (!image || !image.dataset.ftOriginalSrc) return;
    image.src = image.dataset.ftOriginalSrc;
    if (image.dataset.ftOriginalSrcset) image.setAttribute('srcset', image.dataset.ftOriginalSrcset);
    else image.removeAttribute('srcset');
    if (image.dataset.ftOriginalSizes) image.setAttribute('sizes', image.dataset.ftOriginalSizes);
    else image.removeAttribute('sizes');
    card.classList.remove('is-previewing-color');
  };

  const stopInlineVideo = (card, reset = true) => {
    if (!card) return;
    const layer = card.querySelector('[data-ft-inline-video]');
    const video = layer?.querySelector('video');
    const trigger = card.querySelector('[data-ft-inline-video-trigger]');
    if (video) {
      video.pause();
      if (reset) { try { video.currentTime = 0; } catch (error) {} }
      video.onended = null;
    }
    if (layer) layer.hidden = true;
    card.classList.remove('is-inline-video-playing');
    trigger?.setAttribute('aria-pressed', 'false');
  };
  const toggleInlineVideo = (trigger) => {
    const card = trigger?.closest('[data-ft-product-card]');
    const layer = card?.querySelector('[data-ft-inline-video]');
    const video = layer?.querySelector('video');
    if (!card || !layer || !video) return;
    const isPlaying = card.classList.contains('is-inline-video-playing') && !video.paused;
    getCards().forEach((otherCard) => { if (otherCard !== card && otherCard.classList.contains('is-inline-video-playing')) stopInlineVideo(otherCard); });
    if (isPlaying) return stopInlineVideo(card);
    restoreSwatchProductImage(card);
    layer.hidden = false;
    card.classList.add('is-inline-video-playing');
    trigger.setAttribute('aria-pressed', 'true');
    try { video.currentTime = 0; } catch (error) {}
    video.onended = () => stopInlineVideo(card);
    const promise = video.play();
    if (promise?.catch) promise.catch(() => stopInlineVideo(card, false));
  };

  let enhanceRaf = 0;
  const scheduleEnhancements = () => {
    if (enhanceRaf) return;
    enhanceRaf = requestAnimationFrame(() => {
      enhanceRaf = 0;
      normalizeWishlistButtons();
      hydrateFamilySwatches();
    });
  };

  const closeQuickshopMenus = (except = null) => {
    document.querySelectorAll('.ft-product-card--conversion [data-ft-quickshop-options]:not([hidden]), .ft-product-card--metro [data-ft-quickshop-options]:not([hidden])').forEach((panel) => {
      if (panel === except) return;
      panel.hidden = true;
      const toggle = panel.closest('.ft-grid-quickshop-form')?.querySelector('[data-ft-quickshop-toggle]');
      toggle?.setAttribute('aria-expanded', 'false');
    });
  };

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closeQuickshopMenus();
  });

  document.addEventListener('click', (event) => {
    const inlineVideoTrigger = event.target.closest?.('[data-ft-inline-video-trigger]');
    if (inlineVideoTrigger?.closest('.ft-product-card--conversion')) {
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      toggleInlineVideo(inlineVideoTrigger);
      return;
    }

    const quickToggle = event.target.closest?.('[data-ft-quickshop-toggle]');
    if (quickToggle?.closest('.ft-product-card--conversion, .ft-product-card--metro')) {
      event.preventDefault();
      event.stopPropagation();
      const panel = quickToggle.closest('.ft-grid-quickshop-form')?.querySelector('[data-ft-quickshop-options]');
      if (!panel) return;
      const shouldOpen = panel.hidden;
      closeQuickshopMenus();
      panel.hidden = !shouldOpen;
      quickToggle.setAttribute('aria-expanded', String(shouldOpen));
      if (shouldOpen) {
        // Keep long size menus inside the visible viewport on narrow carousels.
        // The menu sits outside the clipped product image, but opens upwards.
        const roomAbove = Math.max(70, quickToggle.getBoundingClientRect().top - 14);
        panel.style.maxHeight = `${Math.floor(Math.min(325, window.innerHeight * 0.5, roomAbove))}px`;
        panel.querySelector('button:not(:disabled), a')?.focus({ preventScroll: true });
      } else {
        panel.style.removeProperty('max-height');
      }
      return;
    }

    const quickOption = event.target.closest?.('[data-ft-grid-size-option]');
    if (quickOption?.closest('.ft-product-card--conversion, .ft-product-card--metro')) {
      event.preventDefault();
      event.stopPropagation();
      if (quickOption.disabled) return;
      const form = quickOption.closest('form');
      const input = form?.querySelector('[data-ft-grid-variant-input]');
      const submit = form?.querySelector('[data-ft-grid-add-button]');
      const variantId = quickOption.dataset.variantId || '';
      if (!input || !variantId || !submit) return;
      input.value = variantId;
      input.disabled = false;
      closeQuickshopMenus();
      // Use Sleek's existing product-form submit handler; no custom cart API.
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submit);
      else submit.click();
      return;
    }

    const wishlistTrigger = event.target.closest?.('.ft-card-wishlist-slot button, .ft-card-wishlist-slot a, .ft-card-wishlist-slot wishlist-button-collection, .ft-card-wishlist-slot xb-wishlist-button-collection, .ft-card-wishlist-slot .wk-button, .ft-card-wishlist-slot .swym-button');
    if (wishlistTrigger?.closest('.ft-product-card--conversion, .ft-product-card--metro')) {
      [40, 160, 420].forEach((delay) => window.setTimeout(normalizeWishlistButtons, delay));
    }

    const clickedMenu = event.target.closest?.('[data-ft-quickshop-options]');
    // BIS app links must keep their original click so AMP can open its modal.
    if (!clickedMenu) closeQuickshopMenus();
    else if (event.target.closest?.('.BIS_trigger')) closeQuickshopMenus();
  }, true);

  document.addEventListener('pointerover', (event) => {
    const familyContainer = event.target.closest?.('[data-ft-family-swatches]');
    if (familyContainer?.closest('[data-ft-product-card]')) hydrateRemoteFamilyForContainer(familyContainer);
    const link = event.target.closest?.('[data-ft-family-swatch-link][data-ft-preview-image]');
    if (link?.closest('[data-ft-product-card]')) previewSwatchProductImage(link);
  });
  document.addEventListener('pointerout', (event) => {
    const link = event.target.closest?.('[data-ft-family-swatch-link][data-ft-preview-image]');
    if (!link?.closest('[data-ft-product-card]')) return;
    const next = event.relatedTarget;
    if (next && link.contains(next)) return;
    const nextLink = next?.closest?.('[data-ft-family-swatch-link][data-ft-preview-image]');
    if (nextLink && nextLink.closest('[data-ft-product-card]') === link.closest('[data-ft-product-card]')) return;
    restoreSwatchProductImage(link.closest('[data-ft-product-card]'));
  });
  document.addEventListener('focusin', (event) => {
    const familyContainer = event.target.closest?.('[data-ft-family-swatches]');
    if (familyContainer?.closest('[data-ft-product-card]')) hydrateRemoteFamilyForContainer(familyContainer);
    const link = event.target.closest?.('[data-ft-family-swatch-link][data-ft-preview-image]');
    if (link?.closest('[data-ft-product-card]')) previewSwatchProductImage(link);
  });
  document.addEventListener('focusout', (event) => {
    const link = event.target.closest?.('[data-ft-family-swatch-link][data-ft-preview-image]');
    if (!link?.closest('[data-ft-product-card]')) return;
    const next = event.relatedTarget;
    const nextLink = next?.closest?.('[data-ft-family-swatch-link][data-ft-preview-image]');
    if (nextLink && nextLink.closest('[data-ft-product-card]') === link.closest('[data-ft-product-card]')) return;
    restoreSwatchProductImage(link.closest('[data-ft-product-card]'));
  });

  if ('MutationObserver' in window) {
    const observer = new MutationObserver((mutations) => {
      const relevant = mutations.some((mutation) => {
        const target = mutation.target?.nodeType === 1 ? mutation.target : mutation.target?.parentElement;
        if (target?.closest?.('[data-ft-swatch-row]')) return false;
        if (target?.closest?.('[data-ft-product-card]')) return true;
        return Array.from(mutation.addedNodes || []).some((node) => {
          if (node.nodeType !== 1) return false;
          return node.matches?.('[data-ft-product-card], [data-ft-family-swatches]')
            || node.querySelector?.('[data-ft-product-card], [data-ft-family-swatches]');
        });
      });
      if (relevant) scheduleEnhancements();
    });
    const startObserver = () => {
      if (!document.body) return;
      observer.observe(document.body, { childList: true, subtree: true });
    };
    if (document.body) startObserver();
    else document.addEventListener('DOMContentLoaded', startObserver, { once: true });
  }

  window.addEventListener('load', scheduleEnhancements, { once: true });
  document.addEventListener('shopify:section:load', scheduleEnhancements);
  document.addEventListener('shopify:section:select', scheduleEnhancements);
  scheduleEnhancements();
  window.setTimeout(scheduleEnhancements, 600);
})();
